import getEwsRequestDateString from './utils/getEwsRequestDateString';
import ApplyConversationActionContext from 'owa-mail-store/lib/store/schema/ApplyConversationActionContext';
import ApplyConversationActionRequest from 'owa-service/lib/contract/ApplyConversationActionRequest';
import ApplyConversationActionResponseMessage from 'owa-service/lib/contract/ApplyConversationActionResponseMessage';
import ConversationAction from 'owa-service/lib/contract/ConversationAction';
import FlagType from 'owa-service/lib/contract/FlagType';
import applyConversationActionRequest from 'owa-service/lib/factory/applyConversationActionRequest';
import conversationAction from 'owa-service/lib/factory/conversationAction';
import folderIdFactory from 'owa-service/lib/factory/folderId';
import itemId from 'owa-service/lib/factory/itemId';
import targetFolderIdFactory from 'owa-service/lib/factory/targetFolderId';
import applyConversationActionOperation from 'owa-service/lib/operation/applyConversationActionOperation';
import { getJsonRequestHeader } from 'owa-service/lib/Utils';

/**
 * Create the flag conversationAction for the given conversation
 * @param conversationContext the conversation context to be flagged
 * @param flagType the flag type to apply
 * @param folderId the sourceFolderId
 * @returns the generated ConversationAction
 */
function createFlagConversationAction(
    conversationContext: ApplyConversationActionContext,
    flagType: FlagType,
    folderId: string
): ConversationAction {
    const flagAction = conversationAction({
        Action: 'Flag',
        ConversationId: itemId({ Id: conversationContext.conversationId }),
        Flag: flagType,
        ConversationLastSyncTime: getEwsRequestDateString(
            conversationContext.conversationLastSyncTimeStamp
        ),
    });

    // folderId can be null in search mode.
    if (folderId) {
        const targetFolderId = targetFolderIdFactory({
            BaseFolderId: folderIdFactory({
                Id: folderId,
            }),
        });

        flagAction.ContextFolderId = targetFolderId;
    }

    return flagAction;
}

/**
 * Configure the request body
 * @param conversationIds the conversation ids
 * @param flagType the flag type to apply
 * @param folderId the folder id
 * @returns an ApplyConversationActionRequest
 */
function configureRequestBody(
    conversationContexts: ApplyConversationActionContext[],
    flagType: FlagType,
    folderId: string
): ApplyConversationActionRequest {
    const conversationActions = conversationContexts.map(conversationContext =>
        createFlagConversationAction(conversationContext, flagType, folderId)
    );

    return applyConversationActionRequest({
        ConversationActions: conversationActions,
    });
}

/**
 * Sends out the service request to set flag on conversation
 * @param conversationContexts the conversation contexts to be deleted
 * @param flagType the flag type to apply
 * @param folderId the source folder id
 * @returns a Promise that handles the service request response
 */
export function flagConversationService(
    conversationContexts: ApplyConversationActionContext[],
    flagType: FlagType,
    folderId: string
): Promise<ApplyConversationActionResponseMessage> {
    const requestBody = configureRequestBody(conversationContexts, flagType, folderId);

    return applyConversationActionOperation({
        Header: getJsonRequestHeader(),
        Body: requestBody,
    }).then(response => response.Body.ResponseMessages.Items[0]);
}
